/* Shared lifecycle for the theme's Swipers. No timers while hidden; one
 * observer/listener per page, and explicit disposal before section replacement. */
(() => {
  if (window.WoolieCarousels) return;

  const entries = new Map();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver(changes => {
      changes.forEach(change => {
        const entry = entries.get(change.target);
        if (!entry) return;
        entry.visible = change.isIntersecting;
        if (entry.visible) entry.failed = false;
        sync(entry);
      });
    }, { threshold: 0 })
    : null;
  // Loading slightly ahead leaves the visible frame ready without starting
  // autoplay off screen. A closed drawer never triggers this request.
  const nearObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(changes => {
      changes.forEach(change => {
        const entry = entries.get(change.target);
        if (!entry) return;
        entry.near = change.isIntersecting;
        if (entry.near) entry.failed = false;
        sync(entry);
      });
    }, { rootMargin: '400px 0px', threshold: 0 })
    : null;

  function canMount(entry) {
    return (entry.near || entry.visible || entry.immediate) && !document.hidden &&
      (!entry.drawer || entry.drawer.classList.contains('active')) &&
      (!entry.media || entry.media.matches);
  }

  function construct(entry, SwiperClass) {
    // Variant changes, drawer replacements and section unloads may happen while
    // the shared request is in flight. Never resurrect their disposed entries.
    if (entries.get(entry.element) !== entry || !entry.element.isConnected ||
        !canMount(entry) || entry.instance) return;
    entry.instance = new SwiperClass(entry.element, entry.options);
    entry.resumeAutoplay = false;
    if (entry.selectedSlide != null) selectSlide(entry);
    sync(entry);
  }

  function selectSlide(entry) {
    const instance = entry.instance;
    if (!instance || instance.destroyed || entry.selectedSlide == null) return;
    if (entry.options.loop) instance.slideToLoop(entry.selectedSlide, 0);
    else instance.slideTo(entry.selectedSlide, 0);
    entry.selectedSlide = null;
  }

  function disposeInstance(entry) {
    if (entry.instance && !entry.instance.destroyed) entry.instance.destroy(true, true);
    entry.instance = null;
  }

  function sync(entry) {
    if (!entry.element.isConnected) {
      dispose(entry);
      return;
    }
    // The desktop and mobile carousels must never coexist as active instances.
    if (entry.media && !entry.media.matches) {
      disposeInstance(entry);
      return;
    }
    const active = entry.visible && !document.hidden &&
      (!entry.drawer || entry.drawer.classList.contains('active'));
    if (!entry.instance && canMount(entry) && !entry.loading && !entry.failed) {
      // Keep Swiper's own pointer/touch handlers enabled (notably hover pause).
      // Construction happens only in an active viewport; reduced motion stops
      // autoplay synchronously below before its first scheduled frame.
      if (typeof window.Swiper === 'function') {
        construct(entry, window.Swiper);
      } else {
        entry.loading = true;
        window.WoolieSwiper.load().then(SwiperClass => {
          entry.loading = false;
          construct(entry, SwiperClass);
        }).catch(() => {
          entry.loading = false;
          entry.failed = true;
          // The static, horizontally scrollable markup remains usable. Retry
          // only on fresh visibility or interaction, never in a polling loop.
        });
      }
    }
    if (!entry.instance || entry.instance.destroyed) return;
    const autoplay = entry.instance.autoplay;
    if (!autoplay || !entry.options.autoplay) return;
    if (!active || motion.matches) {
      if (autoplay.running) {
        entry.resumeAutoplay = true;
        autoplay.stop();
      }
    } else if (entry.resumeAutoplay) {
      entry.resumeAutoplay = false;
      autoplay.start();
    }
  }

  function dispose(entry) {
    disposeInstance(entry);
    if (observer) observer.unobserve(entry.element);
    if (nearObserver) nearObserver.unobserve(entry.element);
    if (entry.media) entry.media.removeEventListener('change', entry.onMediaChange);
    entries.delete(entry.element);
  }

  function within(root, callback) {
    entries.forEach(entry => {
      if (root === entry.element || root.contains(entry.element)) callback(entry);
    });
  }

  window.WoolieCarousels = {
    mount(element, options, context = {}) {
      if (!element || !element.isConnected) return null;
      const previous = entries.get(element);
      if (previous) {
        sync(previous);
        return previous.instance;
      }
      const entry = {
        element, options, drawer: context.drawer, instance: null,
        visible: !observer, near: !nearObserver, immediate: Boolean(context.immediate),
        media: context.media ? window.matchMedia(context.media) : null
      };
      entry.onMediaChange = () => sync(entry);
      entries.set(element, entry);
      if (entry.media) entry.media.addEventListener('change', entry.onMediaChange);
      if (observer) observer.observe(element);
      if (nearObserver) nearObserver.observe(element);
      sync(entry);
      return entry.instance;
    },
    refreshWithin(root) { within(root, sync); },
    activateWithin(root, slideIndex) {
      within(root, entry => {
        entry.near = true;
        entry.failed = false;
        if (slideIndex != null) entry.selectedSlide = slideIndex;
        sync(entry);
        selectSlide(entry);
      });
    },
    destroyWithin(root) { within(root, dispose); }
  };

  document.addEventListener('visibilitychange', () => entries.forEach(sync));
  motion.addEventListener('change', () => entries.forEach(sync));
  document.addEventListener('shopify:section:unload', event => within(event.target, dispose));

  // Custom elements reconnect after Theme Editor/Section Rendering updates;
  // unlike inline DOMContentLoaded handlers, they also cover dynamically added sections.
  class WoolieBannerCarousel extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-woolie-banner-slider]').forEach(element => {
        window.WoolieCarousels.mount(element, {
          loop: true,
          navigation: {
            nextEl: element.querySelector('.swiper-button-next'),
            prevEl: element.querySelector('.swiper-button-prev')
          },
          pagination: {
            el: element.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'bullets'
          },
          autoplay: { delay: 5000 }
        }, { media: element.dataset.carouselMedia });
      });
    }
    disconnectedCallback() { window.WoolieCarousels.destroyWithin(this); }
  }
  customElements.define('woolie-banner-carousel', WoolieBannerCarousel);

  class WoolieSectionCarousel extends HTMLElement {
    connectedCallback() {
      const element = this.querySelector('[data-woolie-carousel]');
      const config = this.querySelector('[data-carousel-options]');
      if (!element || !config) return;
      let options;
      try { options = JSON.parse(config.textContent); } catch (_) { return; }
      if (options.navigation) options.navigation = {
        ...(options.navigation === true ? {} : options.navigation),
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      };
      if (options.pagination) options.pagination = {
        ...(options.pagination === true ? {} : options.pagination),
        el: this.querySelector('.swiper-pagination')
      };
      window.WoolieCarousels.mount(element, options);
      if (!this.onEngage) {
        this.onEngage = () => window.WoolieCarousels.activateWithin(this);
        this.onBlockSelect = event => {
          const slide = event.target.closest('.swiper-slide');
          const currentElement = this.querySelector('[data-woolie-carousel]');
          if (!slide || !currentElement) return;
          const originalIndex = slide.getAttribute('data-swiper-slide-index');
          const index = originalIndex == null
            ? Array.from(currentElement.querySelectorAll('.swiper-slide')).indexOf(slide)
            : Number(originalIndex);
          if (index < 0) return;
          window.WoolieCarousels.activateWithin(this, index);
        };
      }
      this.addEventListener('pointerenter', this.onEngage);
      this.addEventListener('focusin', this.onEngage);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
    }
    disconnectedCallback() {
      this.removeEventListener('pointerenter', this.onEngage);
      this.removeEventListener('focusin', this.onEngage);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      window.WoolieCarousels.destroyWithin(this);
    }
  }
  customElements.define('woolie-section-carousel', WoolieSectionCarousel);
})();
